import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, ExternalLink, Search, BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "All",
  "Agentic AI",
  "Contact Center AI",
  "Enterprise AI Strategy",
  "Tutorials",
  "Leadership Insights"
];

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchArticles = useCallback(async () => {
    try {
      const params = {};
      if (selectedCategory !== "All") {
        params.category = selectedCategory;
      }
      const response = await axios.get(`${API}/articles`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filteredArticles = articles.filter((article) =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="pt-20" data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Blog & Articles
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
              Insights on AI Strategy & Implementation
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed">
              Deep dives into Agentic AI, Contact Center Modernization, and Enterprise AI Strategy. 
              Practical insights from real-world implementations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="articles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-6 mb-12">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-50 border-zinc-200"
                data-testid="search-articles-input"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    selectedCategory === category
                      ? "bg-royal-blue-800 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                  data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-zinc-500">Loading articles...</p>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="article-card rounded-sm overflow-hidden"
                  data-testid={`article-card-${index}`}
                >
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary" className="bg-royal-blue-100 text-royal-blue-800">
                        {article.category}
                      </Badge>
                      {article.is_newsletter && (
                        <Badge variant="outline" className="text-zinc-500">
                          Newsletter
                        </Badge>
                      )}
                    </div>
                    
                    {article.external_link ? (
                      <a 
                        href={article.external_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="group"
                      >
                        <h3 className="font-display text-xl font-semibold text-zinc-950 mb-2 group-hover:text-royal-blue-800 transition-colors flex items-start gap-2">
                          {article.title}
                          <ExternalLink size={16} className="flex-shrink-0 mt-1 opacity-50" />
                        </h3>
                      </a>
                    ) : (
                      <Link to={`/blog/${article.slug}`}>
                        <h3 className="font-display text-xl font-semibold text-zinc-950 mb-2 hover:text-royal-blue-800 transition-colors">
                          {article.title}
                        </h3>
                      </Link>
                    )}
                    
                    <p className="text-zinc-600 text-sm mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="tag-badge text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                      <span className="text-sm text-zinc-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(article.published_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      {article.external_link ? (
                        <a 
                          href={article.external_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-royal-blue-800 hover:text-royal-blue-700 flex items-center gap-1"
                        >
                          Read on LinkedIn
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        <Link 
                          to={`/blog/${article.slug}`}
                          className="text-sm font-medium text-royal-blue-800 hover:text-royal-blue-700 flex items-center gap-1"
                        >
                          Read more
                          <ArrowRight size={14} />
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-zinc-50 rounded-sm" data-testid="no-articles">
              <BookOpen className="mx-auto mb-4 text-zinc-400" size={48} />
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-2">
                No Articles Found
              </h3>
              <p className="text-zinc-600">
                {searchQuery 
                  ? "Try adjusting your search or filter criteria."
                  : "Check back soon for new content."}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
